import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { toast } from "react-toastify";

import {
  Button,
  PageTitle,
  Section,
  Badge,
  IconButton,
  Modal,
  Loading,
} from "../../components";
import { AddIcon, EditIcon, VisibilityIcon } from "../../assets/icons";
import useSupplyService from "../../services/api/supplyService";
import dateFormatter from "../../util/dateFormatter";
import getImageUrl from "../../util/getImageUrl";
import { enums } from "../../constants/enum";
import "./SuppliesPage.scss";

function SuppliesPage() {
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const { ref, inView } = useInView();
  const navigate = useNavigate();

  const { queryKey, getAllSupplyOrders } = useSupplyService();

  const { data, isLoading, error, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: [queryKey],
      queryFn: ({ pageParam = 1 }) => getAllSupplyOrders("", pageParam),
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (lastPage?.totalPages > lastPage?.currentPage) {
          return lastPage?.currentPage + 1;
        }
        return undefined;
      },
      retry: 1,
    });

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  const handleViewSlip = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowModal(true);
  };

  if (isLoading) return <Loading variant="fullScreen" />;
  if (error) {
    toast.error(
      error?.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE
    );
    navigate(-1, { replace: true });
    return null;
  }
  return (
    <>
      <div className="page supplies-page">
        <PageTitle text="Supplies">
          <Link to="/supplies/add">
            <Button text="Add New Supply Record" icon={<AddIcon />} />
          </Link>
        </PageTitle>

        <Section title="All Supplies" className="supply-details">
          <div className="supply-list">
            <div className="table-title">
              <h3>Suppliers</h3>
            </div>
            <div className="table-content">
              <table>
                <thead>
                  <tr>
                    <th>Supplier Name</th>
                    <th>Items</th>
                    <th>Payments</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.pages.map(({ supplierOrders }) =>
                    (supplierOrders || []).map((order) => (
                      <tr key={order._id}>
                        <td>{order.supplier?.name || "N/A"}</td>
                        <td className="itemDetails">
                          {order.items?.map(({ item, quantity, _id }, idx) => (
                            <table key={idx}>
                              <tbody>
                                <tr>
                                  <td>
                                    <Badge text="Item" />{" "}
                                    {item?.itemName || "N/A"}
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <Badge text="Quantity" /> {quantity}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          ))}
                        </td>
                        <td className="itemDetails payments">
                          {order.payments?.map((payment, idx) => (
                            <table key={idx}>
                              <tbody>
                                <tr>
                                  <td>
                                    <Badge text="Amount" />{" "}
                                    {payment.amount?.toFixed(2)} LKR
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <Badge text="Date" />{" "}
                                    {dateFormatter(payment.date)}
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <Badge text="Method" /> {payment.method}
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <Badge text="Payment Slip" />
                                    {payment?.paymentSlip ? (
                                      <IconButton
                                        icon={<VisibilityIcon />}
                                        type="button"
                                        inverted
                                        className="payment-slip-view"
                                        onClick={() => {
                                          const url = getImageUrl(
                                            payment.paymentSlip.fileName
                                          );
                                          if (
                                            payment.paymentSlip.fileType?.startsWith(
                                              "image"
                                            )
                                          ) {
                                            handleViewSlip(url);
                                          } else {
                                            window.open(url, "_blank");
                                          }
                                        }}
                                      />
                                    ) : (
                                      " N/A"
                                    )}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          ))}
                        </td>
                        <td>
                          <Link to={`/supplies/edit/${order._id}`}>
                            <Button text="Edit" prefixIcon={<EditIcon />} />
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                ref={ref}
              >
                <Loading isLoading={isFetchingNextPage} variant="inline" />
              </div>
            </div>
          </div>
        </Section>
      </div>

      <Modal
        className="payment-slip-modal"
        title="Payment Slip"
        close={!showModal}
        onClose={() => setShowModal(false)}
      >
        <div className="payment-slip-image-container">
          <img src={selectedImage} alt="Payment Slip" />
        </div>
      </Modal>
    </>
  );
}

export default SuppliesPage;
